import { useEffect, useState } from "react";
import axios from "axios";
import { Heart, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { API } from "./admin-utils";

export function ItemLikesModal({ open, onOpenChange, item }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !item) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    axios
      .get(`${API}/admin/items/${item.id}/likes`)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch(() => {
        if (!cancelled) setError("Failed to load likes");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, item]);

  const maxCount = data ? Math.max(1, ...data.by_location.map((l) => l.count)) : 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[80vh]" data-testid="item-likes-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-red-500 fill-red-500" />
            Likes by Location
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="text-center py-8">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#1aa59a]" />
          </div>
        )}

        {error && <div className="text-red-500 text-sm text-center py-4">{error}</div>}

        {data && !loading && (
          <div className="space-y-4">
            <div className="border-l-4 border-[#1aa59a] pl-3">
              <h3 className="font-medium text-sm text-gray-700">{data.item_name}</h3>
              <p className="text-2xl font-bold text-[#1aa59a]">
                {data.total} <span className="text-sm font-normal text-gray-500">total likes</span>
              </p>
            </div>

            {data.by_location.length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-6">
                No locations created yet.
              </p>
            ) : (
              <div className="space-y-2">
                <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Breakdown
                </h4>
                <div className="space-y-1.5">
                  {data.by_location.map((loc) => {
                    const pct = (loc.count / maxCount) * 100;
                    return (
                      <div key={loc.slug} className="flex items-center gap-3" data-testid={`likes-row-${loc.slug}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-sm font-medium truncate">{loc.name}</span>
                            <span
                              className={`text-sm font-semibold ml-2 ${
                                loc.count > 0 ? "text-[#1aa59a]" : "text-gray-400"
                              }`}
                            >
                              {loc.count}
                            </span>
                          </div>
                          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-[#1aa59a] rounded-full transition-all"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <p className="text-xs text-gray-400 italic">
              Use this data to optimize stock levels at each location.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
